import { DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import * as userContentsApi from '@apis/admin-user-contents';
import * as commonApi from '@apis/common';
import * as api from '@apis/product-collection';
import * as discountCouponApi from '@apis/discount-coupon';
import * as feedApi from '@apis/feeds-v2';
import FileUploadButton from '@components/common/FileUploadButton';
import { SelectDiscountCouponModal } from '@components/discount-coupon/SelectDiscountCouponModal';
import { CoupangSectionsManager } from '@components/product-collection/CoupangSectionsManager';
import EditProductCollectionSearchItemsView from '@components/feed/EditProductCollectionSearchItemsView';
import { ProductCollectionPreview } from '@components/product-collection/ProductCollectionPreview';
import SelectProductModal from '@components/product/SelectProductModal';
import { SortableDetailImage } from '@components/product/ProductEditView';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppSocket } from '@hooks/appSocket';
import { useAuthentication } from '@hooks/authentication';
import { DiscountCoupon } from '@models/discount-coupon';
import { ProductCategory, ProductModel, ProductStatus } from '@models/product';
import { ProductCollection, ProductCollectionSearchItem } from '@models/productCollection';
import { UserContent } from '@models/user-contents';
import { useContentLoaderStore } from '@stores/contentLoaderStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useProductCollectionsStore } from '@stores/proudctCollectionsStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { clearEmptyKeysFromObject } from '@utils/dataTransform';
import { cdnImageUrl, resizeImage } from '@utils/image';
import { extractFileName } from '@utils/string';
import CropFileUploadButton from '@components/common/CropFileUploadButton';
import { COLLECTION_SHARE_BASE_URL } from '@variables';
import { Button, Checkbox, Input, Modal, Select, Switch, Table, Tooltip, Upload, message } from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import { RcFile } from 'antd/es/upload';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import { toast } from 'react-toastify';
import ProductCollectionRecommendEditModal from '@components/product-collection/ProductCollectionRecommendEdit';

interface SortableProductItemProps {
  onDeleteProductItem?: (event: any) => void;
  value?: any;
  key: any;
  id: any;
  product: ProductModel;
  checked?: boolean;
  onChecked?: (value: boolean) => void;
}

export const SortableProductItem: React.FC<SortableProductItemProps> = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { onDeleteProductItem, product } = props;
  const onClickRemove = (event: any) => {
    event.preventDefault();
    onDeleteProductItem && onDeleteProductItem(event);
  };

  const exposed = product.display && product.status === ProductStatus.OPEN;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          height: '48px',
          borderBottom: '1px solid #eaeaea',
          width: '760px',
        }}>
        <div>
          <Checkbox
            checked={props.checked}
            onChange={(e) => {
              props.onChecked && props.onChecked(e.target.checked);
            }}></Checkbox>
        </div>
        <div>
          <img
            src={cdnImageUrl(product.thumbnailImage)}
            style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '8px' }}></img>
        </div>
        <div
          style={{
            fontWeight: 'bold',
            color: '#aaaaaa',
            width: '160px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {product.sellerName}
        </div>
        <div style={{ width: '320px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {product.title}
        </div>
        <div style={{ fontWeight: 'bold' }}>{`${product.fee?.toLocaleString()}원`}</div>
        <div style={{ whiteSpace: 'pre-wrap' }}>{exposed ? '✅' : '❌'}</div>
        <Button style={{ marginLeft: '12px' }} onClick={onClickRemove}>
          <DeleteOutlined style={{ color: 'red' }} />
        </Button>
      </div>
    </div>
  );
};

function getReviewRateAvg(record: Pick<UserContent, 'board' | 'rates'>): number | null {
  if (record.board !== 'review') {
    return null;
  }
  const total =
    (record.rates?.rate1 || 0) + (record.rates?.rate2 || 0) + (record.rates?.rate3 || 0) + (record.rates?.rate4 || 0);
  return Number((total / 4).toFixed(2));
}

interface SortableReviewItemProps {
  id: string;
  userContentId: string;
  content?: UserContent;
  isLast?: boolean;
  onDelete?: (userContentId: string) => void;
}

const SortableReviewItem: React.FC<SortableReviewItemProps> = ({ id, userContentId, content, isLast, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const reviewAvg = content ? getReviewRateAvg(content) : null;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'stretch',
        width: '760px',
        maxWidth: '100%',
        backgroundColor: '#fff',
        borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
      }}>
      <div
        {...attributes}
        {...listeners}
        title="드래그하여 순서 변경"
        style={{
          flexShrink: 0,
          width: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'grab',
          userSelect: 'none',
          backgroundColor: '#f7f8fa',
          borderRight: '1px solid #e8e8e8',
          color: '#bfbfbf',
        }}>
        <HolderOutlined style={{ fontSize: '16px' }} />
      </div>
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: 0,
          padding: '10px 12px',
        }}>
        {content?.images?.[0] ? (
          <img
            src={cdnImageUrl(content.images[0])}
            alt=""
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '4px',
              objectFit: 'cover',
              backgroundColor: '#f0f0f0',
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '4px',
              backgroundColor: '#f5f5f5',
              flexShrink: 0,
            }}
          />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: '13px',
              color: 'rgba(0, 0, 0, 0.88)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {content?.title || (content?.text || '').replace(/<[^>]*>/g, ' ').slice(0, 48) || '-'}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', lineHeight: 1.35 }}>
            {`${content?.publisher?.nickname || content?.publisher?.name || '-'} · ${content?.product?.title || '-'}`}
          </div>
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: '11px',
              color: '#999',
              marginTop: '2px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {userContentId}
          </div>
        </div>
        <div style={{ flexShrink: 0, width: '64px', textAlign: 'center' }}>
          {content && reviewAvg != null ? (
            <Tooltip
              overlay={
                <table>
                  <tbody>
                    <tr>
                      <td>친화력</td>
                      <td>전문성</td>
                      <td>준비성</td>
                      <td>시간준수</td>
                      <td>아이 만족도</td>
                    </tr>
                    <tr>
                      <td>{`${content.rates?.rate1}`}</td>
                      <td>{`${content.rates?.rate2}`}</td>
                      <td>{`${content.rates?.rate3}`}</td>
                      <td>{`${content.rates?.rate4}`}</td>
                      <td>{`${content.rates?.rate5}`}</td>
                    </tr>
                  </tbody>
                </table>
              }>
              <span style={{ fontWeight: 600, cursor: 'default' }}>평점: {reviewAvg}</span>
            </Tooltip>
          ) : (
            <span style={{ color: '#999', fontSize: '12px' }}>-</span>
          )}
        </div>
        <Button
          size="small"
          onClick={() => {
            onDelete && onDelete(userContentId);
          }}>
          삭제
        </Button>
      </div>
    </div>
  );
};

interface ProductCollectionEditViewProps {
  productCollectionId: string;
  onClose?: () => void;
  needRefetch?: () => void;
}

interface EditProductCollection extends ProductCollection {
  coverImageUrlTemp?: string;
  detailImagesTemp?: string[];
  thumbnailImageTemp?: string;
}

interface SelectReviewUserContentsModalProps {
  open?: boolean;
  onClose?: () => void;
  onAddUserContents?: (contents: UserContent[]) => void;
}

const SelectReviewUserContentsModal: React.FC<SelectReviewUserContentsModalProps> = (props) => {
  const toPlainText = (value?: string) => {
    if (!value) {
      return '';
    }
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [query, setQuery] = useState<{ page: number; size: number; keyword?: string }>({
    page: 0,
    size: 20,
  });
  const [selectedContents, setSelectedContents] = useState<UserContent[]>([]);

  const reviewContentsQuery = useQuery({
    queryKey: ['admin-review-user-contents', JSON.stringify(query), props.open],
    queryFn: () => userContentsApi.searchUserContents({ ...query, board: 'review' }),
    enabled: !!props.open,
  });

  useEffect(() => {
    if (!props.open) {
      setSelectedContents([]);
      setSearchKeyword('');
      setQuery({ page: 0, size: 20 });
    }
  }, [props.open]);

  const contents = reviewContentsQuery.data?.data?.contents || [];
  const total = reviewContentsQuery.data?.data?.total || 0;
  const currentPage = (reviewContentsQuery.data?.data?.page ?? query.page) + 1;
  const pageSize = reviewContentsQuery.data?.data?.size ?? query.size;

  const TABLE_COLUMNS: ColumnsType<UserContent> = [
    {
      title: '',
      width: 56,
      render: (record) => (
        <Checkbox
          checked={!!selectedContents.find((item) => item._id === record._id)}
          onChange={(e) => {
            const checked = e.target.checked;
            setSelectedContents((prev) => {
              if (checked) {
                if (prev.find((item) => item._id === record._id)) {
                  return prev;
                }
                return [...prev, record];
              }
              return prev.filter((item) => item._id !== record._id);
            });
          }}
        />
      ),
    },
    {
      title: 'ID',
      dataIndex: '_id',
      width: 260,
      render: (_id: string) => <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{_id}</div>,
    },
    {
      title: '대표이미지',
      width: 88,
      render: (record) => {
        const thumbnail = record.images?.[0];
        if (!thumbnail) {
          return <div style={{ color: '#999', fontSize: '12px' }}>-</div>;
        }
        return (
          <img
            src={cdnImageUrl(thumbnail)}
            alt=""
            style={{ width: '52px', height: '52px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #eee' }}
          />
        );
      },
    },
    {
      title: '작성자',
      render: (record) => (
        <div
          style={{
            width: '140px',
            maxWidth: '140px',
            overflow: 'hidden',
          }}>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {record.publisher?.nickname || record.publisher?.name || '-'}
          </div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            {record.publisher?.profile?.phone ? record.publisher.profile.phone.slice(-4) : '-'}
          </div>
        </div>
      ),
      width: 80,
    },
    {
      title: '판매자',
      width: 100,
      render: (record) => (
        <div
          style={{
            width: '120px',
            maxWidth: '120px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {record?.paymentStatement?.seller?.name || '-'}
        </div>
      ),
    },
    {
      title: '상품',
      width: 120,
      render: (record) => (
        <div
          style={{
            width: '100px',
            maxWidth: '100px',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            lineHeight: '18px',
          }}>
          {record?.product?.title || '-'}
        </div>
      ),
    },
    {
      title: '평점',
      width: 72,
      render: (record) => {
        const avg = getReviewRateAvg(record);
        if (avg == null) {
          return <div style={{ color: '#999', fontSize: '12px' }}>-</div>;
        }
        return (
          <Tooltip
            overlay={
              <table>
                <tbody>
                  <tr>
                    <td>친화력</td>
                    <td>전문성</td>
                    <td>준비성</td>
                    <td>시간준수</td>
                    <td>아이 만족도</td>
                  </tr>
                  <tr>
                    <td>{`${record.rates?.rate1}`}</td>
                    <td>{`${record.rates?.rate2}`}</td>
                    <td>{`${record.rates?.rate3}`}</td>
                    <td>{`${record.rates?.rate4}`}</td>
                    <td>{`${record.rates?.rate5}`}</td>
                  </tr>
                </tbody>
              </table>
            }>
            <div style={{ fontWeight: 600, cursor: 'default' }}>{avg}</div>
          </Tooltip>
        );
      },
    },
    {
      title: '제목/본문',
      width: 200,
      render: (record) => {
        const title = toPlainText(record.title) || '-';
        const text = toPlainText(record.text) || '-';
        return (
          <div style={{ maxWidth: '560px' }}>
            <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {title}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: '#666',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {text}
            </div>
          </div>
        );
      },
    },
    {
      title: '베스트 후기',
      width: 80,
      render: (record) => {
        if (!record.bestDate) {
          return <div style={{ color: '#999' }}>-</div>;
        }
        return (
          <div>
            <div style={{ fontWeight: 600, color: '#389e0d' }}>BEST</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{moment(record.bestDate).format('YYYY.MM.DD')}</div>
          </div>
        );
      },
    },
    {
      title: '작성일',
      width: 100,
      render: (record) => moment(record.createdAt).format('YYYY.MM.DD HH:mm'),
    },
  ];

  return (
    <Modal
      open={props.open}
      footer={false}
      onCancel={props.onClose}
      width={1200}
      styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Input
            className={filterActiveClassName(!!query.keyword)}
            style={{ width: '280px' }}
            placeholder="제목/본문/판매자/상품 검색 후 엔터"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyUp={(e) => {
              if (e.code === 'Enter') {
                setQuery((prev) => ({ ...prev, page: 0, keyword: searchKeyword || undefined }));
              }
            }}
          />
          <Button
            onClick={() => {
              setSearchKeyword('');
              setQuery({ page: 0, size: 20 });
            }}>
            검색 초기화
          </Button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div>{`${selectedContents.length}개 선택됨`}</div>
          <Button
            type="primary"
            disabled={selectedContents.length === 0}
            onClick={() => {
              props.onAddUserContents && props.onAddUserContents(selectedContents);
              props.onClose && props.onClose();
            }}>
            후기 추가
          </Button>
        </div>
      </div>
      <Table
        rowKey={(record) => record._id}
        columns={TABLE_COLUMNS}
        dataSource={contents.filter((item) => item?._id)}
        loading={reviewContentsQuery.isLoading}
        tableLayout="fixed"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total,
        }}
        onChange={(pagination) => {
          setQuery((prev) => ({
            ...prev,
            page: (pagination.current || 1) - 1,
            size: pagination.pageSize || prev.size,
          }));
        }}
      />
    </Modal>
  );
};

const ProductCollectionEditView: React.FC<ProductCollectionEditViewProps> = (props) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showProductAdd, setShowProductAdd] = useState<boolean>();
  const [showReviewAdd, setShowReviewAdd] = useState<boolean>();
  const [showEditSearchItems, setShowEditSearchItems] = useState<boolean>();
  const [showDiscountCouponSelect, setShowDiscountCouponSelect] = useState<boolean>();
  const [selectedDiscountCoupons, setSelectedDiscountCoupons] = useState<DiscountCoupon[]>([]);
  const [files, setFiles] = useState<{ [key: string]: File }>({});
  const [modifiedProductCollection, setModifiedProductCollection] = useState<EditProductCollection>(
    {} as EditProductCollection,
  );
  const [changedData, setChangedData] = useState<any>({});
  const [previewCouponCoordinates, setPreviewCouponCoordinates] = useState<{ [couponId: string]: number }>({});
  const [collectionCoupons, setCollectionCoupons] = useState<DiscountCoupon[]>([]);
  const [coverImageHeight, setCoverImageHeight] = useState<number>(600); // 커버 이미지 높이 상태
  const [userContentDetails, setUserContentDetails] = useState<{ [id: string]: UserContent }>({});
  const dialog = useOkCancelDialog();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  const productCollectionQuery = useQuery({
    queryKey: [`product-item-${props.productCollectionId}`],
    queryFn: () => api.getProductCollection(props.productCollectionId!),
    enabled: !!props.productCollectionId,
  });

  const collectionCouponsQuery = useQuery({
    queryKey: ['collection-coupons', props.productCollectionId],
    queryFn: () => discountCouponApi.getDiscountCouponsByCollectionId(props.productCollectionId!),
    enabled: !!props.productCollectionId,
  });

  const productRootCategoriesQuery = useQuery({
    queryKey: ['product-root-categories'],
    queryFn: feedApi.getProductRootCategories,
  });
  const productCategories = productRootCategoriesQuery.data?.data?.data?.filter((item) => item.type !== 'all') || [];

  // 콜백 함수들을 컴포넌트 최상위에서 정의
  const handleYCoordinateChange = useCallback((couponId: string, yValue: number) => {
    setPreviewCouponCoordinates((prev) => {
      const newPreviewCoordinates = {
        ...prev,
        [couponId]: yValue,
      };
      return newPreviewCoordinates;
    });
  }, []);

  const handleCouponUpdate = useCallback(() => {
    collectionCouponsQuery.refetch();
  }, [collectionCouponsQuery]);

  const handleImageChange = useCallback(
    (couponId: string, imageType: 'active' | 'inactive', imageUrl: string) => {
      // 실시간으로 collectionCoupons 상태 업데이트
      setCollectionCoupons((prevCoupons) => {
        return prevCoupons.map((coupon) => {
          if (coupon._id === couponId && modifiedProductCollection?._id) {
            const updatedCollections =
              coupon.applicability.collections?.map((collection) => {
                if (collection.id === modifiedProductCollection._id) {
                  return {
                    ...collection,
                    images: {
                      ...collection.images,
                      [imageType]: imageUrl,
                    },
                  };
                }
                return collection;
              }) || [];

            return {
              ...coupon,
              applicability: {
                ...coupon.applicability,
                collections: updatedCollections,
              },
            };
          }
          return coupon;
        });
      });
    },
    [modifiedProductCollection?._id],
  );

  const mutaionUpdateProduct = useMutation({
    mutationFn: api.updateProductCollection,
    onSuccess: async (res) => {
      setModifiedProductCollection(res.data);
      props.needRefetch && props.needRefetch();
      dialog.open({
        content: '저장되었습니다.',
        type: 'ok',
      });
    },
  });

  const onChangeProperty = (key: string, value: any) => {
    setChangedData((prev: any) => {
      const newProperties = { ...prev };
      newProperties[key] = value;
      return newProperties;
    });
  };

  const onChangeCoverImage = async (files: FileList) => {
    const { fileName } = extractFileName(files[0].name);
    const newFiles = await Promise.all(
      Array.from(files)
        .slice(0, 10)
        .map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 828 });

          return result;
        }),
    );
    const contentTypes = newFiles[0].type.split('/');
    const uploadFileName = `${fileName}.${contentTypes[1]}`;
    const newImageFile = new File([newFiles[0].image], `${fileName}.${contentTypes[1]}`) as RcFile;
    const tempUrl = URL.createObjectURL(newFiles[0].image);
    onChangeProperty('coverImageUrlTemp', tempUrl);
    onChangeProperty('coverImageUrl', uploadFileName);
    setFiles((value) => {
      const newValue = { ...value };
      newValue['coverImageUrl'] = newImageFile;
      return newValue;
    });
  };

  const onChangeThumbnailImage = async (files: FileList) => {
    const { fileName } = extractFileName(files[0].name);
    const newFiles = await Promise.all(
      Array.from(files)
        .slice(0, 1)
        .map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 400 });

          return result;
        }),
    );
    const contentTypes = newFiles[0].type.split('/');
    const uploadFileName = `${fileName}.${contentTypes[1]}`;
    const newImageFile = new File([newFiles[0].image], `${fileName}.${contentTypes[1]}`) as RcFile;
    const tempUrl = URL.createObjectURL(newFiles[0].image);
    onChangeProperty('thumbnailImageTemp', tempUrl);
    onChangeProperty('thumbnailImage', uploadFileName);
    setFiles((value) => {
      const newValue = { ...value };
      newValue['thumbnailImage'] = newImageFile;
      return newValue;
    });
  };

  const onChangeDetailImagesFiles = async (files: FileList) => {
    try {
      const imageFiles: any = {};
      const newFiles = await Promise.all(
        Array.from(files).map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 1200, format: 'image/webp' });
          const { fileName } = extractFileName(file.name);
          const contentTypes = result.type.split('/');
          const imageFile = new File([result.image], `${fileName}.${contentTypes[1]}`) as RcFile;
          const tempUrl = URL.createObjectURL(result.image);

          imageFiles[file.name] = imageFile;

          return { result, fileName: `${fileName}.${contentTypes[1]}`, imageFile, tempUrl };
        }),
      );

      setFiles((value) => {
        const newValue = { ...value, ...imageFiles };
        return newValue;
      });

      const tempUrls: string[] = [];
      const fileNames: string[] = [];

      newFiles.forEach((newFile) => {
        tempUrls.push(newFile.tempUrl);
        fileNames.push(newFile.fileName);
      });

      onChangeProperty('detailImagesTemp', [
        ...(modifiedProductCollection.detailImagesTemp || modifiedProductCollection.detailImages || []),
        ...tempUrls,
      ]);
      onChangeProperty('detailImages', [...(modifiedProductCollection.detailImages || []), ...fileNames]);
    } catch (e: any) {
      if (e.code === 'TOO_LARGE_IMAGE') {
        const type = e.property === 'width' ? '넓이' : '높이';

        dialog.open({
          title: '이미지 크기 오류',
          content: `이미지가 너무 큽니다. 이미지를 나누어서 업로드해주세요.\n[${type}]\n현재 크기: ${e.originSize}px\n기준 크기: ${e.expectedSize}px`,
          type: 'ok',
        });
      } else {
        dialog.open({
          title: '알수없는 오류',
          content: `새로고침 후 다시 시도해주시거나 그래도 안되시면 개발팀에 문의해주세요.`,
          type: 'ok',
        });
      }
    }
  };

  const onDeleteDetailImage = (index: number) => {
    const newDetailImages = [...(modifiedProductCollection.detailImages || [])];
    const newDetailImagesTemp = [
      ...(modifiedProductCollection.detailImagesTemp || modifiedProductCollection.detailImages || []),
    ];

    newDetailImages.splice(index, 1);
    newDetailImagesTemp.splice(index, 1);
    onChangeProperty('detailImagesTemp', newDetailImagesTemp);
    onChangeProperty('detailImages', newDetailImages);
  };

  const onDetailImagesSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = (
        modifiedProductCollection.detailImagesTemp ||
        modifiedProductCollection.detailImages ||
        []
      ).findIndex((item) => item === active.id);
      const newIndex = (
        modifiedProductCollection.detailImagesTemp ||
        modifiedProductCollection.detailImages ||
        []
      ).findIndex((item) => item === over?.id);
      const newDetailImages = arrayMove(modifiedProductCollection.detailImages || [], oldIndex, newIndex);
      const newDetailImagesTemp = arrayMove(
        modifiedProductCollection.detailImagesTemp || modifiedProductCollection.detailImages || [],
        oldIndex,
        newIndex,
      );

      onChangeProperty('detailImages', newDetailImages);
      onChangeProperty('detailImagesTemp', newDetailImagesTemp);
    }
  };

  const onDeleteProductItem = (index: number) => {
    const newProducts = [...modifiedProductCollection.products];
    newProducts.splice(index, 1);
    onChangeProperty('products', newProducts);
  };

  const onProductSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = modifiedProductCollection.products.findIndex((item) => item._id === active.id);
      const newIndex = modifiedProductCollection.products.findIndex((item) => item._id === over?.id);
      const newProducts = arrayMove(modifiedProductCollection.products, oldIndex, newIndex);
      onChangeProperty('products', newProducts);
    }
  };

  const onUserContentsSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const currentUserContentIds: string[] =
      changedData?.userContentIds || modifiedProductCollection.userContentIds || [];
    const oldIndex = currentUserContentIds.findIndex((id) => id === active.id);
    const newIndex = currentUserContentIds.findIndex((id) => id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    onChangeProperty('userContentIds', arrayMove(currentUserContentIds, oldIndex, newIndex));
  };

  useEffect(() => {
    if (productCollectionQuery.data?.data) {
      setModifiedProductCollection(productCollectionQuery.data?.data);
    }
  }, [productCollectionQuery.data?.data]);

  useEffect(() => {
    setModifiedProductCollection((value) => {
      return { ...value, ...changedData };
    });
  }, [changedData]);

  useEffect(() => {
    const userContentIds: string[] = changedData?.userContentIds || modifiedProductCollection.userContentIds || [];
    if (userContentIds.length === 0) {
      setUserContentDetails({});
      return;
    }

    const missingIds = userContentIds.filter((id) => !userContentDetails[id]);
    if (missingIds.length === 0) {
      return;
    }

    (async () => {
      const entries = await Promise.all(
        missingIds.map(async (id) => {
          try {
            const response = await userContentsApi.getUserContent(id);
            return [id, response.data] as const;
          } catch {
            return [id, undefined] as const;
          }
        }),
      );

      setUserContentDetails((prev) => {
        const next = { ...prev };
        entries.forEach(([id, content]) => {
          if (content) {
            next[id] = content;
          }
        });
        return next;
      });
    })();
  }, [changedData?.userContentIds, modifiedProductCollection.userContentIds, userContentDetails]);

  useEffect(() => {
    if (collectionCouponsQuery.data) {
      setCollectionCoupons(collectionCouponsQuery.data);

      // 초기 미리보기 좌표 설정
      const initialCoordinates: { [couponId: string]: number } = {};
      collectionCouponsQuery.data.forEach((coupon: DiscountCoupon) => {
        const collectionData = coupon.applicability?.collections?.find(
          (collection) => collection.id === props.productCollectionId,
        );
        const yValue = collectionData?.coordinates?.y ?? 0;
        initialCoordinates[coupon._id] = yValue;
      });
      setPreviewCouponCoordinates(initialCoordinates);
    }
  }, [collectionCouponsQuery.data, props.productCollectionId]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100dvh',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'white',
        zIndex: 900,
        borderLeft: '1px solid #eaeaea',
      }}>
      <Modal
        width={800}
        open={showEditSearchItems}
        onCancel={() => {
          setShowEditSearchItems(false);
        }}
        footer={
          <div>
            <Button
              onClick={() => {
                setShowEditSearchItems(false);
              }}>
              닫기
            </Button>
          </div>
        }>
        <EditProductCollectionSearchItemsView
          data={changedData.searchItems || modifiedProductCollection.searchItems}
          onChange={(data) => {
            onChangeProperty('searchItems', data);
          }}></EditProductCollectionSearchItemsView>
      </Modal>
      <SelectProductModal
        open={showProductAdd}
        onAddProducts={(products: ProductModel[]) => {
          const candidates: ProductModel[] = [];
          products.forEach((product) => {
            const found = (modifiedProductCollection.products || []).find((item) => item._id === product._id);
            if (!found) {
              candidates.push(product);
            }
          });

          const newProducts = [...candidates, ...modifiedProductCollection.products];
          onChangeProperty('products', newProducts);

          toast.info(`${candidates.length}개의 상품이 추가되었습니다.`, {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            pauseOnFocusLoss: false,
            draggable: true,
            progress: undefined,
            theme: 'light',
          });
        }}
        onClose={() => {
          setShowProductAdd(false);
        }}></SelectProductModal>
      <SelectReviewUserContentsModal
        open={showReviewAdd}
        onAddUserContents={(contents) => {
          const currentUserContentIds = changedData?.userContentIds || modifiedProductCollection.userContentIds || [];
          const nextUserContentIds = Array.from(
            new Set([...currentUserContentIds, ...contents.map((item) => item._id)]),
          );
          const addedCount = nextUserContentIds.length - currentUserContentIds.length;

          onChangeProperty('userContentIds', nextUserContentIds);
          setUserContentDetails((prev) => {
            const next = { ...prev };
            contents.forEach((content) => {
              next[content._id] = content;
            });
            return next;
          });
          toast.info(`${addedCount}개의 후기가 추가되었습니다.`, {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            pauseOnFocusLoss: false,
            draggable: true,
            progress: undefined,
            theme: 'light',
          });
        }}
        onClose={() => {
          setShowReviewAdd(false);
        }}
      />
      <SelectDiscountCouponModal
        open={showDiscountCouponSelect}
        onAddCoupons={(coupons: DiscountCoupon[]) => {
          const candidates: DiscountCoupon[] = [];
          coupons.forEach((coupon) => {
            const found = selectedDiscountCoupons.find((item) => item._id === coupon._id);
            if (!found) {
              candidates.push(coupon);
            }
          });
          setSelectedDiscountCoupons((prev) => {
            const newCoupons = [...prev, ...candidates];
            // changedData에 쿠폰 정보 반영
            setChangedData((prevData: any) => ({
              ...prevData,
              discountCoupons: newCoupons.map((coupon) => ({
                couponId: coupon._id,
                imageUrl: '',
              })),
            }));
            return newCoupons;
          });
          setShowDiscountCouponSelect(false);
        }}
        onClose={() => {
          setShowDiscountCouponSelect(false);
        }}></SelectDiscountCouponModal>
      <div style={{ display: 'flex', height: '100%', gap: '24px' }}>
        <div style={{ flex: '1', minWidth: '0', overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              height: '56px',
              borderBottom: '1px solid #eff1f4',
              fontSize: '17px',
              fontWeight: 'bold',
              color: '#242424',
              paddingLeft: '24px',
            }}>{`${modifiedProductCollection.title || '이름 없음'} 기획전 편집`}</div>
          <div style={{ padding: '12px', height: 'calc(100% - 100px)', overflow: 'auto' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '48px',
              }}>
              <div style={{ flex: '0 0 auto', minWidth: '0' }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>기획전 구분</div>
                  <Select
                    value={changedData?.type || modifiedProductCollection.type || 'general'}
                    style={{ width: 120 }}
                    onChange={(value) => {
                      onChangeProperty('type', value);
                    }}
                    options={[
                      { value: 'search', label: '검색기반' },
                      { value: 'md_pick', label: 'MD PICK' },
                      { value: 'general', label: '일반형' },
                      { value: 'list', label: '리스트형' },
                      { value: 'grid', label: '그리드형' },
                      { value: 'thema', label: '테마' },
                      { value: 'combine', label: '혼합형' },
                      { value: 'coupang-products', label: '쿠팡상품' },
                    ]}
                  />
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>기획전 제목</div>
                  <Input
                    style={{ maxWidth: '428px' }}
                    value={changedData?.title || modifiedProductCollection.title}
                    onChange={(e) => {
                      onChangeProperty('title', e.target.value.substring(0, 20));
                    }}></Input>{' '}
                  <span>{`(${(changedData?.title || modifiedProductCollection.title || '').length} / 20)`}</span>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>카테고리 선택(복수가능)</div>
                  <div style={{ fontSize: '13px', marginTop: '8px', maxWidth: '428px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {productCategories.map((item) => {
                        return (
                          <Checkbox
                            key={item.type}
                            style={{ width: '150px' }}
                            checked={(modifiedProductCollection.categories || []).includes(item.type as any)}
                            onChange={(e) => {
                              let categories = [
                                ...(changedData?.categories || modifiedProductCollection.categories || []),
                              ];

                              if (e.target.checked) {
                                categories.push(item.type as any);
                              } else {
                                categories = categories.filter((cate) => cate !== item.type);
                              }
                              onChangeProperty('categories', categories);
                            }}>
                            {item.label?.ko}
                          </Checkbox>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>유의사항</div>
                  <div style={{ marginTop: '8px' }}>
                    {(changedData?.precautions || modifiedProductCollection.precautions || []).map(
                      (precaution: any, index: number) => (
                        <div
                          key={index}
                          style={{
                            marginBottom: '12px',
                            border: '1px solid #e8eaed',
                            borderRadius: '8px',
                            padding: '12px',
                          }}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '8px',
                            }}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>유의사항 {index + 1}</span>
                            <Button
                              size="small"
                              danger
                              onClick={() => {
                                const newPrecautions = (
                                  changedData?.precautions ||
                                  modifiedProductCollection.precautions ||
                                  []
                                ).filter((_: any, i: number) => i !== index);
                                onChangeProperty('precautions', newPrecautions);
                              }}>
                              삭제
                            </Button>
                          </div>
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>제목</div>
                            <Input
                              placeholder="유의사항 제목을 입력하세요"
                              value={precaution.title}
                              onChange={(e) => {
                                const newPrecautions = [
                                  ...(changedData?.precautions || modifiedProductCollection.precautions || []),
                                ];
                                newPrecautions[index] = { ...precaution, title: e.target.value };
                                onChangeProperty('precautions', newPrecautions);
                              }}
                            />
                          </div>
                          <div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>설명</div>
                            <Input.TextArea
                              placeholder="유의사항 설명을 입력하세요"
                              rows={3}
                              value={precaution.description}
                              onChange={(e) => {
                                const newPrecautions = [
                                  ...(changedData?.precautions || modifiedProductCollection.precautions || []),
                                ];
                                newPrecautions[index] = { ...precaution, description: e.target.value };
                                onChangeProperty('precautions', newPrecautions);
                              }}
                            />
                          </div>
                        </div>
                      ),
                    )}
                    <Button
                      type="dashed"
                      style={{ width: '100%', maxWidth: '428px' }}
                      onClick={() => {
                        const newPrecautions = [
                          ...(changedData?.precautions || modifiedProductCollection.precautions || []),
                          { title: '', description: '' },
                        ];
                        onChangeProperty('precautions', newPrecautions);
                      }}>
                      + 유의사항 추가
                    </Button>
                  </div>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>썸네일 이미지</div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '12px',
                    }}>
                    <div
                      style={{
                        width: '200px',
                        height: '200px',
                        border: '1px solid #eaeaea',
                        overflow: 'hidden',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '8px',
                      }}>
                      {modifiedProductCollection.thumbnailImageTemp || modifiedProductCollection.thumbnailImage ? (
                        <img
                          src={cdnImageUrl(
                            modifiedProductCollection.thumbnailImageTemp ||
                              modifiedProductCollection.thumbnailImage ||
                              '',
                          )}
                          style={{ width: '200px', height: '200px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div>{'NO IMAGE'}</div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <CropFileUploadButton
                      accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
                        .map((ext) => {
                          return '.' + ext;
                        })
                        .join(',')}
                      className="link-button"
                      style={{
                        display: 'flex',
                        alignSelf: 'center',
                        justifyContent: 'center',
                        height: '36px',
                        width: '200px',
                        border: '1px solid #aaaaaa',
                        borderRadius: '8px',
                        lineHeight: '36px',
                      }}
                      modalTitleChoice="썸네일 이미지 업로드"
                      modalTitleCrop="썸네일 이미지 크롭"
                      onChangeFiles={(files) => {
                        onChangeThumbnailImage(files);
                      }}>
                      썸네일 이미지 바꾸기
                    </CropFileUploadButton>
                    {(modifiedProductCollection.thumbnailImageTemp || modifiedProductCollection.thumbnailImage) && (
                      <Button
                        size="small"
                        danger
                        onClick={() => {
                          onChangeProperty('thumbnailImageTemp', '');
                          onChangeProperty('thumbnailImage', '');
                          setFiles((prev) => {
                            const next = { ...prev };
                            delete next.thumbnailImage;
                            return next;
                          });
                        }}>
                        썸네일 삭제
                      </Button>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>커버이미지</div>
                  <div
                    style={{
                      width: '428px',
                      height: '200px',
                      border: '1px solid #eaeaea',
                      overflow: 'hidden',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    {modifiedProductCollection.coverImageUrlTemp || modifiedProductCollection.coverImageUrl ? (
                      <img
                        src={cdnImageUrl(
                          modifiedProductCollection.coverImageUrlTemp || modifiedProductCollection.coverImageUrl,
                        )}
                        style={{ width: '428px', height: '200px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div>{'NO IMAGE'}</div>
                    )}
                  </div>
                  <FileUploadButton
                    accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
                      .map((ext) => {
                        return '.' + ext;
                      })
                      .join(',')}
                    className="link-button"
                    style={{
                      display: 'flex',
                      alignSelf: 'center',
                      justifyContent: 'center',
                      height: '36px',
                      width: '428px',
                      border: '1px solid #aaaaaa',
                      borderRadius: '8px',
                      lineHeight: '36px',
                      marginTop: '4px',
                    }}
                    onChange={(e) => {
                      if (e.target.files) {
                        const files = e.target.files;
                        onChangeCoverImage(files);
                      }
                    }}>
                    커버이미지 바꾸기
                  </FileUploadButton>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>연결 후기</div>
                    <Button
                      onClick={() => {
                        setShowReviewAdd(true);
                      }}>
                      후기 추가
                    </Button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ width: '120px', fontSize: '13px', color: '#555' }}>노출 여부</span>
                    <Switch
                      checked={
                        changedData?.showUserContents !== undefined
                          ? changedData.showUserContents
                          : modifiedProductCollection.showUserContents ?? false
                      }
                      onChange={(checked) => {
                        onChangeProperty('showUserContents', checked);
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <span style={{ width: '120px', fontSize: '13px', color: '#555' }}>리뷰 영역 타이틀</span>
                    <Input
                      style={{ width: '360px' }}
                      placeholder="예) 생생한 후기"
                      value={
                        changedData?.userContentsTitle !== undefined
                          ? changedData.userContentsTitle
                          : modifiedProductCollection.userContentsTitle || ''
                      }
                      onChange={(e) => {
                        onChangeProperty('userContentsTitle', e.target.value);
                      }}
                    />
                  </div>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onUserContentsSortEnd}>
                    <SortableContext
                      items={changedData?.userContentIds || modifiedProductCollection.userContentIds || []}
                      strategy={verticalListSortingStrategy}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          border: '1px solid #e8e8e8',
                          borderRadius: '6px',
                          overflow: 'hidden',
                          maxWidth: '760px',
                          backgroundColor: '#fff',
                        }}>
                        {(changedData?.userContentIds || modifiedProductCollection.userContentIds || []).map(
                          (userContentId: string, index: number, arr: string[]) => {
                            const content = userContentDetails[userContentId];
                            return (
                              <SortableReviewItem
                                key={userContentId}
                                id={userContentId}
                                userContentId={userContentId}
                                content={content}
                                isLast={index === arr.length - 1}
                                onDelete={(targetId) => {
                                  const currentUserContentIds =
                                    changedData?.userContentIds || modifiedProductCollection.userContentIds || [];
                                  onChangeProperty(
                                    'userContentIds',
                                    currentUserContentIds.filter((id: string) => id !== targetId),
                                  );
                                }}
                              />
                            );
                          },
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <div>상세 소개 이미지</div>
                      <Button
                        onClick={async () => {
                          const images =
                            modifiedProductCollection.detailImagesTemp || modifiedProductCollection.detailImages || [];
                          for (let i = 0; i < images.length; i++) {
                            const imageUrl = cdnImageUrl(images[i]);
                            if (imageUrl) {
                              const response = await fetch(imageUrl);
                              response.blob().then((blob) => {
                                resizeImage(blob, {
                                  format: 'image/png',
                                }).then((result) => {
                                  const url = window.URL.createObjectURL(result.image);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `${modifiedProductCollection.title?.slice(0, 10) || '기획전'}_상세_${i}`;
                                  a.click();
                                });
                              });
                            }
                          }
                        }}>
                        전체 다운로드
                      </Button>
                    </div>
                    <FileUploadButton
                      multiple
                      accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
                        .map((ext) => {
                          return '.' + ext;
                        })
                        .join(',')}
                      className="link-button"
                      style={{
                        fontSize: '14px',
                        height: '32px',
                        padding: '4px 15px',
                        borderRadius: '6px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d9d9d9',
                        color: 'rgba(0, 0, 0, 0.88)',
                        boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
                        cursor: 'pointer',
                        marginLeft: '12px',
                      }}
                      onChange={(e) => {
                        if (e.target.files) {
                          const files = e.target.files;
                          onChangeDetailImagesFiles(files);
                        }
                      }}>
                      상세 소개 이미지 추가
                    </FileUploadButton>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginLeft: '12px',
                        flexWrap: 'wrap',
                      }}>
                      <span style={{ fontSize: '14px' }}>표시 형식</span>
                      <Select<'list' | 'grid'>
                        style={{ width: 160 }}
                        value={
                          changedData?.detailImagesLayout !== undefined
                            ? changedData.detailImagesLayout
                            : modifiedProductCollection.detailImagesLayout ?? 'list'
                        }
                        options={[
                          { label: '리스트 (캐러셀)', value: 'list' },
                          { label: '그리드', value: 'grid' },
                        ]}
                        onChange={(value) => onChangeProperty('detailImagesLayout', value)}
                      />
                    </div>
                  </div>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDetailImagesSortEnd}>
                    <SortableContext
                      items={(
                        modifiedProductCollection.detailImagesTemp ||
                        modifiedProductCollection.detailImages ||
                        []
                      ).map((item) => item)}
                      strategy={horizontalListSortingStrategy}>
                      <div style={{ display: 'flex', gap: '4px', overflow: 'auto', maxWidth: '600px' }}>
                        {(
                          modifiedProductCollection.detailImagesTemp ||
                          modifiedProductCollection.detailImages ||
                          []
                        ).map((imageUrl: string, index: number) => {
                          return (
                            <SortableDetailImage
                              key={`image-${imageUrl}-${index}`}
                              id={imageUrl}
                              onDeleteDetailImage={() => {
                                onDeleteDetailImage(index);
                              }}
                              imageUrl={imageUrl}
                            />
                          );
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              </div>
              <div
                style={{
                  maxHeight: 'calc(100vh - 200px)',
                  overflowY: 'auto',
                  flex: '1',
                  minWidth: '300px',
                }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                  연동 쿠폰 목록 (노출 조건: 이미지, 좌표 설정)
                </div>
                {modifiedProductCollection.type !== 'coupang-products' && (
                  <CollectionCouponsView
                    collectionId={modifiedProductCollection?._id}
                    coupons={collectionCoupons}
                    coverImageHeight={coverImageHeight}
                    onYCoordinateChange={handleYCoordinateChange}
                    onCouponUpdate={handleCouponUpdate}
                    onImageChange={handleImageChange}
                  />
                )}
              </div>
            </div>
            {(changedData?.type || modifiedProductCollection?.type) === 'search' && (
              <div style={{ marginTop: '12px' }}>
                <Button
                  style={{ marginLeft: 'auto' }}
                  onClick={() => {
                    setShowEditSearchItems(true);
                  }}>
                  검색 버튼 편집
                </Button>
                <div style={{ display: 'flex', padding: '8px 0', flexWrap: 'wrap', gap: '8px', maxWidth: '500px' }}>
                  {(changedData?.searchItems || modifiedProductCollection.searchItems || []).map(
                    (item: ProductCollectionSearchItem, index: number) => {
                      return (
                        <button key={item.id} className="link-button" style={{ height: '32px' }}>
                          <div
                            style={{
                              display: 'flex',
                              height: '32px',
                              borderRadius: '16px',
                              paddingLeft: '16px',
                              paddingRight: '16px',
                              fontSize: '14px',
                              border: index === 0 ? '1px solid #424242' : '1px solid #e8eaed',
                              color: index === 0 ? '#ffffff' : '#424242',
                              backgroundColor: index === 0 ? '#242424' : '#ffffff',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}>
                            <div>{item.label}</div>
                          </div>
                        </button>
                      );
                    },
                  )}
                </div>
              </div>
            )}
            {(changedData?.type || modifiedProductCollection?.type) !== 'search' &&
              (changedData?.type || modifiedProductCollection?.type) !== 'coupang-products' && (
                <div style={{ marginTop: '12px', width: '740px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Checkbox
                      checked={
                        JSON.stringify(selectedIds.sort()) ===
                        JSON.stringify(
                          (modifiedProductCollection.products || [])
                            .filter((product) => !!product)
                            .map((product) => product._id)
                            .sort(),
                        )
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(
                            (modifiedProductCollection.products || [])
                              .filter((product) => !!product)
                              .map((product) => product._id),
                          );
                        } else {
                          setSelectedIds([]);
                        }
                      }}></Checkbox>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: 'bold' }}>상품 목록</div>
                      <div style={{ fontSize: '13px', marginLeft: '5px' }}>(노출여부: ✅ / ❌)</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span>무작위 노출: </span>
                      <Switch
                        checked={changedData?.shuffle || modifiedProductCollection.shuffle}
                        onChange={(checked) => {
                          onChangeProperty('shuffle', checked);
                        }}></Switch>
                    </div>
                    <Button
                      style={{ marginLeft: 'auto' }}
                      onClick={() => {
                        setShowProductAdd(true);
                      }}>
                      상품 추가
                    </Button>
                  </div>
                  <div>
                    {selectedIds.length > 0 && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                        <div>{`${selectedIds.length}개 선택 항목을`} </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <Button
                            style={{ color: 'red' }}
                            onClick={() => {
                              const newProducts = [...modifiedProductCollection.products];
                              onChangeProperty(
                                'products',
                                newProducts.filter((product) => !selectedIds.includes(product._id)),
                              );
                              setSelectedIds([]);
                            }}>
                            삭제
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onProductSortEnd}>
                    <SortableContext
                      items={(modifiedProductCollection.products || []).map((item) => item._id)}
                      strategy={verticalListSortingStrategy}>
                      {(modifiedProductCollection.products || []).map((item: ProductModel, index: number) => {
                        return (
                          <SortableProductItem
                            key={`image-${item}-${index}`}
                            id={item._id}
                            onDeleteProductItem={() => {
                              onDeleteProductItem(index);
                            }}
                            product={item}
                            onChecked={(checked) => {
                              setSelectedIds((values) => {
                                const newValues = [...values];
                                if (checked) {
                                  if (!newValues.includes(item._id)) {
                                    newValues.push(item._id);
                                  }
                                  return newValues;
                                } else {
                                  return newValues.filter((value) => value !== item._id);
                                }
                              });
                            }}
                            checked={selectedIds.includes(item._id)}
                          />
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                </div>
              )}
            <div style={{ marginTop: '12px', width: '740px' }}>
              {(changedData?.type || modifiedProductCollection?.type) === 'coupang-products' && (
                <>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>쿠팡 상품 섹션 관리</div>
                  <CoupangSectionsManager
                    sections={changedData?.coupangSections || modifiedProductCollection.coupangSections || []}
                    onSectionsChange={(sections) => {
                      onChangeProperty('coupangSections', sections);
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        <div
          style={{
            width: '428px',
            minWidth: '428px',
            overflowY: 'auto',
            position: 'sticky',
            top: '0',
          }}>
          <ProductCollectionPreview
            data={{
              ...modifiedProductCollection,
              coverImageUrl: cdnImageUrl(
                modifiedProductCollection.coverImageUrlTemp || modifiedProductCollection.coverImageUrl,
              ) as string,
              detailImages: modifiedProductCollection.detailImagesTemp || modifiedProductCollection.detailImages || [],
              detailImagesLayout:
                changedData?.detailImagesLayout !== undefined
                  ? changedData.detailImagesLayout
                  : modifiedProductCollection.detailImagesLayout ?? 'list',
            }}
            coupons={collectionCoupons}
            previewCouponCoordinates={previewCouponCoordinates}
            onCoverImageHeightChange={(height) => {
              setCoverImageHeight(height);
            }}></ProductCollectionPreview>
        </div>
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          left: 0,
          height: '44px',
          backgroundColor: '#eaeaea',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '12px',
          paddingRight: '24px',
        }}>
        <Button
          disabled={!changedData}
          onClick={() => {
            const formData: FormData = new FormData();
            const json = { ...changedData };
            if (json.products) {
              json.products = json.products.map((product: ProductModel) => {
                return { _id: product._id };
              });
            }
            formData.append('productCollectionId', props.productCollectionId);
            formData.append('json', JSON.stringify(json));

            Object.values(files).forEach((file) => {
              formData.append('files', file);
            });
            mutaionUpdateProduct.mutate(formData);
          }}>
          저장
        </Button>

        <Button
          onClick={() => {
            props.onClose && props.onClose();
          }}>
          닫기
        </Button>
      </div>
    </div>
  );
};

export const ProductCollectionsPage: React.FC = () => {
  const [shareLinkProductId, setShareLinkProductId] = useState<string>();
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const [previewProductCollection, setPreviewProductCollection] = useState<ProductCollection>();
  const [productCollectionEditId, setProductCollectionEditId] = useState<string>();
  const [showRecommendEditModal, setShowRecommendEditModal] = useState<boolean>(false);
  const searchInputRef = useRef<any>();
  const appSocket = useAppSocket();
  const store = useProductCollectionsStore();
  const loader = useContentLoaderStore();
  const dialog = useOkCancelDialog();
  useAuthentication();

  const generateOneShareLinkMutate = useMutation({
    mutationFn: commonApi.generateProductCollectionShareLink,
  });

  const productCollectionSearchQuery = useQuery({
    queryKey: [`admin-search-product-collections`, JSON.stringify(store.searchQuery)],
    queryFn: () => api.searchProductCollections(store.searchQuery),
  });

  const productRootCategoriesQuery = useQuery({
    queryKey: ['product-root-categories'],
    queryFn: feedApi.getProductRootCategories,
  });
  const productCategories = productRootCategoriesQuery.data?.data?.data?.filter((item) => item.type !== 'all') || [];

  const createCollectionMutation = useMutation({
    mutationFn: api.createProductCollection,
    onSuccess: () => {
      productCollectionSearchQuery.refetch();
      dialog.open({
        content: `새로운 기획전이 추가되었습니다`,
      });
    },
  });

  const deleteCollectionMutation = useMutation({
    mutationFn: api.deleteProductCollection,
    onSuccess: () => {
      productCollectionSearchQuery.refetch();
      toast.info('기획전이 삭제되었습니다', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        theme: 'light',
      });
    },
  });

  useEffect(() => {
    const searchResult = productCollectionSearchQuery.data?.data;
    if (searchResult) {
      store.setProductCollectionsContainer(searchResult);
    }
  }, [productCollectionSearchQuery.data?.data]);

  useEffect(() => {
    appSocket.socket.addSocketEventListener('upload_excel_status', 'upload_excel_status', (data) => {
      const status = (data.status || '').toLowerCase();
      if (status === 'done') {
        productCollectionSearchQuery.refetch();
      }
      if (status === 'done' || status === 'error') {
        loader.off();
      }

      if (data.status) {
        toast.error(data.message, {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          pauseOnFocusLoss: false,
          draggable: true,
          progress: undefined,
          theme: 'light',
        });
      }
    });
  }, []);

  window.onbeforeunload = () => {
    appSocket.socket.removeSocketEventListener('upload_excel_status', 'upload_excel_status');
  };

  const TABLE_COLUMNS: ColumnsType<ProductCollection> = [
    {
      title: 'ID',
      dataIndex: '_id',
      render: (_id) => {
        return (
          <Button
            onClick={() => {
              navigator.clipboard.writeText(_id);
              toast.dark('기획전 ID를 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                pauseOnFocusLoss: false,
                draggable: true,
                progress: undefined,
                bodyStyle: {
                  color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                  textAlign: 'center',
                  fontFamily: 'Pretendard',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px',
                },
                theme: 'dark',
              });
            }}>
            ID 복사
          </Button>
        );
      },
    },
    {
      title: '커버 이미지',
      dataIndex: 'coverImageUrl',
      render: (coverImageUrl) => {
        if (!coverImageUrl) {
          return 'NO IMAGE';
        }
        return (
          <img
            src={cdnImageUrl(coverImageUrl)}
            style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
          />
        );
      },
    },
    {
      title: '구분',
      dataIndex: 'type',
      render: (type) => {
        if (!type) {
          return '-';
        }
        return `${type}`;
      },
    },
    {
      title: '카테고리',
      dataIndex: 'categories',
      width: 200,
      render: (categories) => {
        if (!categories || categories.length === 0) {
          return '-';
        }
        return `${categories
          .map(
            (category: ProductCategory) => productCategories.find((item) => item.type === category)?.label?.ko || '-',
          )
          .join(', ')}`;
      },
    },
    {
      title: '기획전 제목',
      dataIndex: 'title',
      render: (title) => `${title}`,
    },
    {
      title: '상품수',
      width: 80,
      dataIndex: 'products',
      render: (products) => {
        if (!products) {
          return '-';
        }
        return `${products.length}`;
      },
    },
    {
      title: '노출 우선순위',
      dataIndex: 'priority',
      render: (priority) => {
        if (priority) {
          return '-';
        }
        return `${priority}`;
      },
    },
    {
      title: '복제',
      render: (record) => {
        return (
          <Button
            onClick={() => {
              api.getProductCollection(record._id).then((response) => {
                const duplicatedProductCollection = {
                  ...response.data,
                  _id: undefined,
                  meta: undefined,
                  updatedAt: undefined,
                  createdAt: undefined,
                  title: `[복제됨] ${response.data.title}`,
                };
                createCollectionMutation.mutate(clearEmptyKeysFromObject(duplicatedProductCollection));
              });
            }}>
            복제본 생성
          </Button>
        );
      },
    },
    {
      title: '공유링크',
      dataIndex: '_id',
      render: (_id) => {
        const url = `${COLLECTION_SHARE_BASE_URL}/${_id}`;
        return (
          <Button
            onClick={() => {
              navigator.clipboard.writeText(url);
              toast.dark('주소를 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                pauseOnFocusLoss: false,
                draggable: true,
                progress: undefined,
                bodyStyle: {
                  color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                  textAlign: 'center',
                  fontFamily: 'Pretendard',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px',
                },
                theme: 'dark',
              });
            }}>
            복사
          </Button>
        );
      },
    },
    {
      title: '수정',
      dataIndex: '_id',
      render: (_id: string) => {
        return (
          <Button
            key={`${_id}`}
            onClick={() => {
              setProductCollectionEditId(_id);
            }}>
            수정
          </Button>
        );
      },
    },
    {
      title: '삭제',
      dataIndex: '_id',
      render: (_id: string) => {
        return (
          <Button
            style={{ color: 'red' }}
            key={`${_id}`}
            onClick={() => {
              dialog.open({
                content: '삭제하면 복구할 수 없습니다.\n해당 기획전을 삭제하시겠습니까?',
                onConfirm: () => {
                  deleteCollectionMutation.mutate(_id);
                },
              });
            }}>
            삭제
          </Button>
        );
      },
    },
    {
      title: '미리보기',
      dataIndex: '_id',
      render: (_id: string) => {
        return (
          <Button
            onClick={() => {
              api.getProductCollection(_id).then((response) => {
                setPreviewProductCollection(response.data);
              });
            }}>
            미리보기
          </Button>
        );
      },
    },
    {
      title: '생성/수정',
      render: (record) => {
        return (
          <div>
            <div>{record.createdAt ? moment(new Date(record.createdAt)).format('YYYY.MM.DD HH:mm') : '-'}</div>
            <div style={{ fontWeight: 'bold' }}>
              {record.updatedAt ? moment(new Date(record.updatedAt)).format('YYYY.MM.DD HH:mm') : '-'}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      {showRecommendEditModal && (
        <ProductCollectionRecommendEditModal
          onClose={() => {
            setShowRecommendEditModal(false);
          }}
        />
      )}
      <div className="page-header">
        <div>기획전 관리</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Input
            placeholder="신규 기획전 ID"
            onChange={(e) => {
              setShareLinkProductId(e.target.value);
            }}></Input>
          <button
            disabled={generateOneShareLinkMutate.isPending}
            className="link-button"
            style={{
              color: 'white',
              fontWeight: 'bold',
              width: '240px',
              display: 'flex',
              justifyContent: 'center',
              height: '40px',
              padding: '4px',
              backgroundColor: '#2259a1',
              alignItems: 'center',
              borderRadius: '6px',
            }}
            onClick={() => {
              if (shareLinkProductId) {
                generateOneShareLinkMutate.mutate(shareLinkProductId);
              }
            }}>
            {generateOneShareLinkMutate.isPending && <ClipLoader size={'20px'} color={'#ffffff'}></ClipLoader>}
            {!generateOneShareLinkMutate.isPending && '신규기획전 공유링크 생성'}
          </button>
          <button
            className="link-button"
            style={{
              color: 'white',
              fontWeight: 'bold',
              width: '240px',
              display: 'flex',
              justifyContent: 'center',
              height: '40px',
              padding: '4px',
              backgroundColor: '#a12259',
              alignItems: 'center',
              borderRadius: '6px',
            }}
            onClick={() => {
              setShowRecommendEditModal(true);
            }}>
            신규 기획전 관리
          </button>
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div style={{ display: 'flex', gap: '12px', width: '100%', height: '52px', alignItems: 'center' }}>
            <FilterSelect
              inactiveValue="all"
              defaultValue="all"
              style={{ width: 120 }}
              onChange={(value) => {
                store.updateSearchQuery({ type: value });
              }}
              options={[
                { value: 'all', label: '전체' },
                { value: 'md_pick', label: 'MD PICK' },
                { value: 'general', label: '일반형' },
                { value: 'list', label: '리스트형' },
                { value: 'grid', label: '그리드형' },
                { value: 'thema', label: '테마' },
                { value: 'combine', label: '혼합형' },
              ]}
            />
            <Input
              className={filterActiveClassName(!!store.searchQuery.keyword)}
              ref={searchInputRef}
              style={{ width: '240px' }}
              value={searchKeyword}
              placeholder={'검색어 입력 후 엔터'}
              onChange={(e) => {
                setSearchKeyword(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.code === 'Enter' && searchKeyword) {
                  store.updateSearchQuery({ keyword: searchKeyword });
                }
              }}></Input>

            <div style={{ display: 'flex', marginLeft: 'auto', gap: '12px', alignItems: 'center' }}>
              <Button
                onClick={() => {
                  createCollectionMutation.mutate({ type: 'general', title: '신규 기획전' });
                }}>
                신규 기획전 추가
              </Button>
              <Button
                onClick={() => {
                  setSearchKeyword(undefined);
                  store.resetSearchQuery();
                }}>
                검색 초기화
              </Button>
            </div>
          </div>
          <Table
            columns={TABLE_COLUMNS}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 280px)' }}
            style={{ whiteSpace: 'pre-wrap' }}
            rowKey={(record) => record?._id}
            dataSource={store.searchResultContainer.contents.filter((product) => product?._id)}
            pagination={{
              current: (store.searchResultContainer.page || 0) + 1,
              pageSize: store.searchResultContainer.size || 20,
              total: store.searchResultContainer.total,
            }}
            loading={productCollectionSearchQuery.isLoading}
            onChange={(e) => {
              store.updateSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
            }}
          />
        </div>
        {productCollectionEditId && (
          <ProductCollectionEditView
            productCollectionId={productCollectionEditId}
            onClose={() => {
              setProductCollectionEditId(undefined);
            }}
            needRefetch={() => {
              productCollectionSearchQuery.refetch();
            }}
          />
        )}
        {previewProductCollection && (
          <div
            style={{
              width: '428px',
              height: '100vh',
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'white',
              zIndex: 900,
              borderLeft: '1px solid #eaeaea',
            }}>
            <div style={{ height: '44px', backgroundColor: '#eaeaea', display: 'flex', alignItems: 'center' }}>
              <Button
                onClick={() => {
                  setPreviewProductCollection(undefined);
                }}>
                닫기
              </Button>
            </div>
            <ProductCollectionPreview data={previewProductCollection}></ProductCollectionPreview>
          </div>
        )}
      </div>
    </div>
  );
};

// 기획전과 연동된 쿠폰 목록을 표시하는 컴포넌트
const CollectionCouponsView: React.FC<{
  collectionId?: string;
  coupons: DiscountCoupon[];
  onYCoordinateChange?: (couponId: string, yValue: number) => void;
  onCouponUpdate?: () => void;
  onImageChange?: (couponId: string, imageType: 'active' | 'inactive', imageUrl: string) => void;
  coverImageHeight?: number;
}> = ({
  collectionId,
  coupons,
  onYCoordinateChange = () => {},
  onCouponUpdate,
  onImageChange,
  coverImageHeight = 600,
}) => {
  const [yCoordinates, setYCoordinates] = useState<{ [couponId: string]: number }>({});
  const [couponImages, setCouponImages] = useState<{ [couponId: string]: { active: string; inactive: string } }>({});
  const [updatingCoupons, setUpdatingCoupons] = useState<Set<string>>(new Set());
  const dialog = useOkCancelDialog();

  const updateCouponMutation = useMutation({
    mutationFn: discountCouponApi.updateDiscountCoupon,
    onSuccess: () => {
      onCouponUpdate && onCouponUpdate(); // 상위 컴포넌트에서 쿠폰 데이터 새로고침
      dialog.open({
        content: '쿠폰이 성공적으로 업데이트되었습니다.',
        type: 'ok',
      });
    },
    onError: (error: any) => {
      dialog.open({
        content: `쿠폰 업데이트에 실패했습니다: ${error.message}`,
        type: 'ok',
      });
    },
    onSettled: (data, error, variables) => {
      setUpdatingCoupons((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.couponId);
        return newSet;
      });
    },
  });

  // props로 받은 쿠폰 데이터가 변경되면 y좌표 및 이미지 값들을 상태에 설정
  useEffect(() => {
    if (coupons && coupons.length > 0 && collectionId) {
      const initialYCoordinates: { [couponId: string]: number } = {};
      const initialImages: { [couponId: string]: { active: string; inactive: string } } = {};

      coupons.forEach((coupon: DiscountCoupon) => {
        const collectionData = coupon.applicability?.collections?.find((collection) => collection.id === collectionId);
        const yValue = collectionData?.coordinates?.y ?? 0; // null/undefined 모두 0으로 처리
        const images = collectionData?.images || { active: '', inactive: '' };
        initialYCoordinates[coupon._id] = yValue;
        initialImages[coupon._id] = images;
      });
      setYCoordinates(initialYCoordinates);
      setCouponImages(initialImages);
    }
  }, [coupons, collectionId]);

  // 초기값을 미리보기에 설정하는 별도 useEffect
  useEffect(() => {
    if (yCoordinates && Object.keys(yCoordinates).length > 0) {
      Object.entries(yCoordinates).forEach(([couponId, yValue]) => {
        onYCoordinateChange(couponId, yValue);
      });
    }
  }, [yCoordinates, onYCoordinateChange]);

  const handleYCoordinateChange = (couponId: string, value: number) => {
    // 커버 이미지 높이에서 쿠폰 높이(40px)를 뺀 값을 최대값으로 제한
    const maxY = Math.max(0, coverImageHeight - 40);
    const limitedValue = Math.max(0, Math.min(value, maxY));

    setYCoordinates((prev) => {
      const newCoordinates = {
        ...prev,
        [couponId]: limitedValue,
      };
      return newCoordinates;
    });

    // 실시간 미리보기 업데이트
    onYCoordinateChange(couponId, limitedValue);
  };

  const handleImageChange = (couponId: string, imageType: 'active' | 'inactive', value: string) => {
    setCouponImages((prev) => ({
      ...prev,
      [couponId]: {
        ...prev[couponId],
        [imageType]: value,
      },
    }));

    // 상위 컴포넌트에 이미지 변경 알림
    if (onImageChange) {
      onImageChange(couponId, imageType, value);
    }
  };

  const handleImageUpload = async (file: RcFile, couponId: string, imageType: 'active' | 'inactive') => {
    try {
      message.loading({ content: '이미지 업로드 중...', key: `upload-${couponId}-${imageType}` });

      const response = await commonApi.uploadImage(file);
      const filePath = response.data.filePath;
      if (!filePath) {
        throw new Error('파일 업로드에 실패했습니다.');
      }
      const imageUrl = cdnImageUrl(filePath);
      if (!imageUrl) {
        throw new Error('이미지 URL 생성에 실패했습니다.');
      }

      handleImageChange(couponId, imageType, imageUrl);

      message.success({ content: '이미지 업로드 완료!', key: `upload-${couponId}-${imageType}` });
      return false; // prevent default upload behavior
    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      message.error({ content: '이미지 업로드에 실패했습니다.', key: `upload-${couponId}-${imageType}` });
      return false;
    }
  };

  const handleSaveCoupon = async (coupon: DiscountCoupon) => {
    if (!collectionId) return;

    setUpdatingCoupons((prev) => new Set(prev).add(coupon._id));

    const yValue = yCoordinates[coupon._id] || 0;
    const images = couponImages[coupon._id] || { active: '', inactive: '' };

    // ratio 값 계산 (0-1 범위, 이미지 높이에 대한 비율)
    const ratioValue = coverImageHeight > 0 ? yValue / coverImageHeight : 0;

    // 기존 applicability를 복사하고 collections 업데이트
    const updatedApplicability = { ...coupon.applicability };
    const existingCollections = updatedApplicability.collections || [];

    // 현재 컬렉션의 인덱스 찾기
    const collectionIndex = existingCollections.findIndex((collection) => collection.id === collectionId);

    if (collectionIndex >= 0) {
      // 기존 컬렉션 데이터 업데이트
      existingCollections[collectionIndex] = {
        ...existingCollections[collectionIndex],
        coordinates: {
          y: yValue,
          ratio: ratioValue,
        },
        images: images,
      };
    } else {
      // 새로운 컬렉션 데이터 추가
      existingCollections.push({
        id: collectionId,
        coordinates: {
          y: yValue,
          ratio: ratioValue,
        },
        images: images,
      });
    }

    updatedApplicability.collections = existingCollections;

    updateCouponMutation.mutate({
      couponId: coupon._id,
      body: {
        name: coupon.name,
        description: coupon.description,
        issuanceType: coupon.issuanceType.toLowerCase(),
        discountType: coupon.discountType.toLowerCase(),
        discountAmount: coupon.discountAmount,
        discountPercent: coupon.discountPercent,
        maxDiscountAmount: coupon.maxDiscountAmount,
        minPurchaseAmount: coupon.minPurchaseAmount,
        isStackable: coupon.isStackable,
        usageLimitPerUser: coupon.usageLimitPerUser,
        issueLimitPerUser: coupon.issueLimitPerUser,
        usageLimit: coupon.usageLimit,
        quantity: coupon.quantity,
        issueStartDate: coupon.issueStartDate,
        issueEndDate: coupon.issueEndDate,
        validFrom: coupon.validFrom,
        validUntil: coupon.validUntil,
        appOnly: coupon.appOnly,
        applicability: updatedApplicability,
        issuedBy: coupon.issuedBy,
        costBearer: coupon.costBearer,
      },
    });
  };

  const COUPON_COLUMNS: ColumnsType<DiscountCoupon> = [
    {
      title: '쿠폰명',
      dataIndex: 'name',
      width: 100,
      render: (name) => <div style={{ fontWeight: 'bold' }}>{name}</div>,
    },
    {
      title: '할인 정보',
      width: 150,
      render: (record: DiscountCoupon) => {
        let discountText = '';
        const discountType = record.discountType;
        if (record.discountType === 'percentage') {
          discountText = `${record.discountPercent}%\n(최대 ${record.maxDiscountAmount?.toLocaleString()}원)`;
        } else {
          discountText = `${record.discountAmount?.toLocaleString()}원`;
        }
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{discountType === 'percentage' ? '퍼센트' : '고정금액'}</div>
            {discountText}
          </div>
        );
      },
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: 80,
      render: (status) => {
        const color = status === 'active' ? '#3fb547' : status === 'inactive' ? '#bd334a' : '#6e6e6e';
        const text = status === 'active' ? '활성' : status === 'inactive' ? '비활성' : '만료';
        return (
          <div className="badge" style={{ backgroundColor: color }}>
            {text}
          </div>
        );
      },
    },
    {
      title: '발급/사용',
      width: 120,
      render: (record: DiscountCoupon) => (
        <div>
          <div>
            발급: {record.issuedCount || 0} / {record.quantity}
          </div>
          <div>
            사용: {record.usedCount || 0} / {record.usageLimit}
          </div>
        </div>
      ),
    },
    {
      title: '기간',
      width: 150,
      render: (record: DiscountCoupon) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>[발급]</div>
          <div>{record.issueStartDate ? moment(record.issueStartDate).format('YYYY-MM-DD') : '-'}</div>
          <div>{record.issueEndDate ? `~ ${moment(record.issueEndDate).format('YYYY-MM-DD')}` : ''}</div>
          <div style={{ fontWeight: 'bold' }}>[유효]</div>
          <div>{record.validFrom ? moment(record.validFrom).format('YYYY-MM-DD') : '-'}</div>
          <div>{record.validUntil ? `~ ${moment(record.validUntil).format('YYYY-MM-DD')}` : ''}</div>
        </div>
      ),
    },
    {
      title: 'y좌표',
      width: 100,
      render: (record: DiscountCoupon) => {
        const currentValue = yCoordinates[record._id];

        return (
          <Input
            type="number"
            value={currentValue !== undefined ? currentValue : 0}
            onChange={(e) => {
              const inputValue = e.target.value;

              if (inputValue === '' || inputValue === null || inputValue === undefined) {
                handleYCoordinateChange(record._id, 0);
              } else {
                const numValue = Number(inputValue);
                if (!isNaN(numValue) && numValue >= 0) {
                  handleYCoordinateChange(record._id, Math.floor(numValue));
                }
              }
            }}
            onBlur={(e) => {
              // blur 시에도 값 검증
              const value = Number(e.target.value) || 0;
              if (currentValue !== value) {
                handleYCoordinateChange(record._id, value);
              }
            }}
            style={{ width: '80px' }}
            min={0}
            max={Math.max(0, coverImageHeight - 40)}
            step={1}
            placeholder="0"
          />
        );
      },
    },
    {
      title: '이미지',
      width: 200,
      render: (record: DiscountCoupon) => {
        const currentImages = couponImages[record._id] || { active: '', inactive: '' };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  marginBottom: '4px',
                  fontWeight: 'bold',
                }}>
                활성 이미지:
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => handleImageUpload(file, record._id, 'active')}
                  style={{ flex: 1 }}>
                  <Button size="small" style={{ fontSize: '11px' }}>
                    파일 선택
                  </Button>
                </Upload>
                {currentImages.active && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <img
                      src={cdnImageUrl(currentImages.active)}
                      alt="활성 이미지 미리보기"
                      style={{
                        width: '30px',
                        height: '20px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                      }}
                    />
                    <Button
                      size="small"
                      type="text"
                      danger
                      onClick={() => handleImageChange(record._id, 'active', '')}
                      style={{ fontSize: '10px', padding: '2px 4px' }}>
                      삭제
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  marginBottom: '4px',
                  fontWeight: 'bold',
                }}>
                비활성 이미지:
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => handleImageUpload(file, record._id, 'inactive')}
                  style={{ flex: 1 }}>
                  <Button size="small" style={{ fontSize: '11px' }}>
                    파일 선택
                  </Button>
                </Upload>
                {currentImages.inactive && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <img
                      src={currentImages.inactive}
                      alt="비활성 이미지 미리보기"
                      style={{
                        width: '30px',
                        height: '20px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                      }}
                    />
                    <Button
                      size="small"
                      type="text"
                      danger
                      onClick={() => handleImageChange(record._id, 'inactive', '')}
                      style={{ fontSize: '10px', padding: '2px 4px' }}>
                      삭제
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: '저장',
      width: 80,
      render: (record: DiscountCoupon) => (
        <Button
          type="primary"
          size="small"
          loading={updatingCoupons.has(record._id)}
          disabled={updatingCoupons.has(record._id)}
          onClick={() => handleSaveCoupon(record)}>
          저장
        </Button>
      ),
    },
  ];

  if (!collectionId) {
    return <div style={{ color: '#999', padding: '20px' }}>기획전을 선택해주세요.</div>;
  }

  if (coupons.length === 0) {
    return <div style={{ color: '#999', padding: '20px' }}>연동된 쿠폰이 없습니다.</div>;
  }

  return (
    <div style={{ marginTop: '12px' }}>
      <Table
        columns={COUPON_COLUMNS}
        bordered
        dataSource={coupons}
        rowKey="_id"
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        style={{ maxHeight: '300px', overflow: 'auto', whiteSpace: 'pre-wrap', minWidth: '500px', maxWidth: '1000px' }}
      />
    </div>
  );
};

export default ProductCollectionsPage;
